/**
 * Global Link Handler
 * Manages all links across the entire Shopify website
 * Version: 1.0
 */

(function($) {
  'use strict';

  // Main Link Handler Object
  const LinkHandler = {
    // Configuration
    config: {
      // External link behavior
      externalLinkTarget: '_blank',
      externalLinkRel: 'noopener noreferrer',
      showExternalIndicator: true,
      
      // Internal link behavior
      smoothScroll: true,
      smoothScrollDuration: 600,
      
      // Analytics tracking
      trackLinks: true,
      trackExternalLinks: true,
      
      // Link styling
      addExternalClass: true,
      externalIconClass: 'link-external-icon',
      
      // Excluded elements
      excludeSelectors: [
        '.no-link-handler',
        '.btn-add-to-cart',
        '[role="button"]',
        '.mini-cart-link'
      ],
      
      // Event tracking
      enableEventTracking: true,
      
      // Debug mode
      debug: false
    },

    // Initialize the link handler
    init: function() {
      if (this.config.debug) {
        console.log('LinkHandler: Initializing...');
      }

      this.bindLinks();
      this.handleExternalLinks();
      this.handleInternalLinks();
      this.handleAnchorLinks();
      this.handleProductLinks();
      this.handleCategoryLinks();
      this.attachAnalytics();

      if (this.config.debug) {
        console.log('LinkHandler: Initialization complete');
      }
    },

    /**
     * Bind all links on the page
     */
    bindLinks: function() {
      const self = this;
      
      $(document).on('click', 'a', function(e) {
        const $link = $(this);
        
        // Check if link should be processed
        if (self.shouldExcludeLink($link)) {
          return true;
        }

        // Determine link type and handle accordingly
        const href = $link.attr('href');
        
        if (!href || href === '#' || href === 'javascript:void(0)' || href.startsWith('javascript:')) {
          return true;
        }

        // External link
        if (self.isExternalLink(href)) {
          self.handleExternalLinkClick(e, $link);
        }
        
        // Anchor link (same page)
        else if (href.startsWith('#')) {
          self.handleAnchorLinkClick(e, $link);
        }
        
        // Internal link
        else {
          self.handleInternalLinkClick(e, $link);
        }
      });
    },

    /**
     * Check if link should be excluded from processing
     */
    shouldExcludeLink: function($link) {
      const excludeSelectors = this.config.excludeSelectors.join(', ');
      
      if ($link.closest(excludeSelectors).length > 0) {
        return true;
      }

      if ($link.attr('data-no-handler') === 'true') {
        return true;
      }

      if ($link.parents('.no-link-handler').length > 0) {
        return true;
      }

      return false;
    },

    /**
     * Check if link is external
     */
    isExternalLink: function(href) {
      if (href.startsWith('http://') || href.startsWith('https://')) {
        const linkHost = this.getDomain(href);
        const currentHost = window.location.hostname;
        return linkHost !== currentHost;
      }
      return false;
    },

    /**
     * Extract domain from URL
     */
    getDomain: function(url) {
      try {
        return new URL(url).hostname;
      } catch (e) {
        return '';
      }
    },

    /**
     * Handle external link clicks
     */
    handleExternalLinkClick: function(e, $link) {
      const href = $link.attr('href');

      // Track the click if enabled
      if (this.config.trackLinks && this.config.trackExternalLinks) {
        this.trackLink('external', href, $link.text());
      }

      // Add external link styling if not already present
      if (this.config.addExternalClass && !$link.hasClass('external-link')) {
        $link.addClass('external-link');
      }

      // Add external icon if configured
      if (this.config.showExternalIndicator && !$link.find('.' + this.config.externalIconClass).length) {
        $link.append('<span class="' + this.config.externalIconClass + '"></span>');
      }

      // Set target and rel attributes
      if (!$link.attr('target')) {
        $link.attr('target', this.config.externalLinkTarget);
      }

      if (!$link.attr('rel')) {
        $link.attr('rel', this.config.externalLinkRel);
      }

      if (this.config.debug) {
        console.log('LinkHandler: External link clicked', href);
      }
    },

    /**
     * Handle internal link clicks
     */
    handleInternalLinkClick: function(e, $link) {
      const href = $link.attr('href');

      // Don't prevent default - let normal navigation happen
      // Track the click if enabled
      if (this.config.trackLinks) {
        this.trackLink('internal', href, $link.text());
      }

      if (this.config.debug) {
        console.log('LinkHandler: Internal link clicked', href);
      }
    },

    /**
     * Handle anchor link clicks
     */
    handleAnchorLinkClick: function(e, $link) {
      const href = $link.attr('href');
      const target = $(href);

      if (target.length === 0) {
        return true; // Link doesn't point to existing element
      }

      e.preventDefault();

      // Track the click if enabled
      if (this.config.trackLinks) {
        this.trackLink('anchor', href, $link.text());
      }

      // Smooth scroll if enabled
      if (this.config.smoothScroll) {
        this.smoothScrollToElement(target);
      } else {
        this.scrollToElement(target);
      }

      if (this.config.debug) {
        console.log('LinkHandler: Anchor link clicked', href);
      }
    },

    /**
     * Smooth scroll to element
     */
    smoothScrollToElement: function($element) {
      const offset = this.getElementOffset($element);

      $('html, body').animate(
        { scrollTop: offset },
        this.config.smoothScrollDuration,
        'easeInOutQuad'
      );
    },

    /**
     * Jump to element instantly
     */
    scrollToElement: function($element) {
      const offset = this.getElementOffset($element);
      window.scrollTo(0, offset);
    },

    /**
     * Get element offset accounting for sticky header
     */
    getElementOffset: function($element) {
      const offset = $element.offset().top;
      const stickyHeader = $('header.sticky, .sticky-header, [data-sticky="true"]');
      const headerHeight = stickyHeader.length ? stickyHeader.outerHeight() : 0;

      return Math.max(0, offset - headerHeight - 20); // 20px extra padding
    },

    /**
     * Handle product links with quick view option
     */
    handleProductLinks: function() {
      const self = this;

      $(document).on('click', '.product-card a, .product-link', function(e) {
        const $link = $(this);
        const href = $link.attr('href');

        // Check for quick view button
        if ($link.hasClass('quick-view-btn')) {
          e.preventDefault();
          self.openProductQuickView($link, href);
          return;
        }

        // Normal product link - let default action happen
        if (self.config.trackLinks) {
          self.trackLink('product', href, $link.text());
        }
      });
    },

    /**
     * Handle category links with mega menu support
     */
    handleCategoryLinks: function() {
      const self = this;

      $(document).on('click', '.category-link, [data-category-link="true"]', function(e) {
        const $link = $(this);
        const href = $link.attr('href');

        if (self.config.trackLinks) {
          self.trackLink('category', href, $link.text());
        }

        // Let default navigation happen
      });
    },

    /**
     * Open product quick view
     */
    openProductQuickView: function($link, href) {
      if (this.config.debug) {
        console.log('LinkHandler: Opening quick view for', href);
      }

      // Trigger custom event for quick view
      $(document).trigger('quickview:open', [href]);
    },

    /**
     * Attach analytics tracking
     */
    attachAnalytics: function() {
      if (!this.config.enableEventTracking) {
        return;
      }

      const self = this;

      // Track all link interactions
      $(document).on('linkHandler:trackLink', function(e, type, url, text) {
        self.sendAnalyticsEvent(type, url, text);
      });
    },

    /**
     * Track link click
     */
    trackLink: function(type, url, text) {
      if (this.config.debug) {
        console.log('LinkHandler: Tracking', type, url, text);
      }

      // Trigger custom event
      $(document).trigger('linkHandler:trackLink', [type, url, text]);

      // Send to Google Analytics if available
      if (typeof gtag !== 'undefined') {
        gtag('event', 'link_click', {
          'link_type': type,
          'link_url': url,
          'link_text': text
        });
      }

      // Send to any custom analytics
      this.sendToCustomAnalytics(type, url, text);
    },

    /**
     * Send analytics event to tracking service
     */
    sendAnalyticsEvent: function(type, url, text) {
      // Custom analytics integration point
      if (typeof window.customAnalytics !== 'undefined') {
        window.customAnalytics.track('link_click', {
          type: type,
          url: url,
          text: text,
          timestamp: new Date().toISOString()
        });
      }
    },

    /**
     * Send to custom analytics service
     */
    sendToCustomAnalytics: function(type, url, text) {
      // Override this method to integrate with your analytics service
    },

    /**
     * Manage external links
     */
    handleExternalLinks: function() {
      const self = this;
      const externalLinkSelector = 'a[href^="http"]:not([href*="' + window.location.hostname + '"])';

      $(externalLinkSelector).each(function() {
        const $link = $(this);

        if (self.shouldExcludeLink($link)) {
          return;
        }

        // Add classes and attributes
        if (self.config.addExternalClass) {
          $link.addClass('external-link');
        }

        // Set target and rel
        if (!$link.attr('target')) {
          $link.attr('target', self.config.externalLinkTarget);
        }

        if (!$link.attr('rel')) {
          $link.attr('rel', self.config.externalLinkRel);
        }

        // Add icon if configured
        if (self.config.showExternalIndicator && !$link.find('.' + self.config.externalIconClass).length) {
          $link.append('<span class="' + self.config.externalIconClass + '"></span>');
        }
      });
    },

    /**
     * Manage internal links
     */
    handleInternalLinks: function() {
      const self = this;
      const internalLinkSelector = 'a[href^="/"], a[href*="' + window.location.hostname + '"]';

      $(internalLinkSelector).each(function() {
        const $link = $(this);

        if (self.shouldExcludeLink($link)) {
          return;
        }

        // Add internal link class
        $link.addClass('internal-link');
      });
    },

    /**
     * Manage anchor links
     */
    handleAnchorLinks: function() {
      const self = this;
      const anchorLinkSelector = 'a[href^="#"]';

      $(anchorLinkSelector).each(function() {
        const $link = $(this);
        const href = $link.attr('href');

        if (self.shouldExcludeLink($link) || href === '#' || href === '#!' || !href) {
          return;
        }

        // Add anchor class
        $link.addClass('anchor-link');

        // Add smooth scroll class
        if (self.config.smoothScroll) {
          $link.addClass('smooth-scroll');
        }
      });
    },

    /**
     * Public API: Update configuration
     */
    updateConfig: function(newConfig) {
      $.extend(this.config, newConfig);
      
      if (this.config.debug) {
        console.log('LinkHandler: Configuration updated', this.config);
      }

      // Re-initialize with new config
      this.init();
    },

    /**
     * Public API: Add excluded selector
     */
    excludeSelector: function(selector) {
      if (!this.config.excludeSelectors.includes(selector)) {
        this.config.excludeSelectors.push(selector);
      }
    },

    /**
     * Public API: Remove excluded selector
     */
    removeExcludedSelector: function(selector) {
      this.config.excludeSelectors = this.config.excludeSelectors.filter(s => s !== selector);
    },

    /**
     * Public API: Get all links of a specific type
     */
    getLinks: function(type) {
      let selector = '';

      switch(type) {
        case 'external':
          selector = 'a[href^="http"]:not([href*="' + window.location.hostname + '"])';
          break;
        case 'internal':
          selector = 'a[href^="/"], a[href*="' + window.location.hostname + '"]';
          break;
        case 'anchor':
          selector = 'a[href^="#"]';
          break;
        case 'product':
          selector = '.product-card a, .product-link';
          break;
        case 'category':
          selector = '.category-link, [data-category-link="true"]';
          break;
        default:
          selector = 'a';
      }

      return $(selector);
    },

    /**
     * Public API: Destroy the link handler
     */
    destroy: function() {
      $(document).off('click', 'a');
      
      if (this.config.debug) {
        console.log('LinkHandler: Destroyed');
      }
    }
  };

  /**
   * jQuery Plugin Wrapper
   */
  $.fn.linkHandler = function(method) {
    if (LinkHandler[method]) {
      return LinkHandler[method].apply(LinkHandler, Array.prototype.slice.call(arguments, 1));
    } else if (typeof method === 'object' || !method) {
      return LinkHandler.updateConfig(method || {});
    } else {
      $.error('Method ' + method + ' does not exist on jQuery.linkHandler');
    }
  };

  /**
   * Initialize on document ready
   */
  $(document).ready(function() {
    LinkHandler.init();

    // Expose to window for global access
    window.LinkHandler = LinkHandler;
  });

})(jQuery);